import { z } from 'zod'
import { RankDeltaDirection } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { App, AppCategoryRecommendation, Category } from '@/payload-types'
import type { RankChange } from '@/components/ui/RankIndicator'

// How far back to read category_rank_snapshot rows. Snapshots are only written
// when the order changes (deduped), so 100 spans a long history — plenty to find
// an app's last actual move. Mirrors the /best page reader.
export const APP_RANKING_SNAPSHOT_LOOKBACK = 100

// Snapshot-derived rank deltas only read as a trend for apps currently in the top N
// of the list; a manual `rankDeltaOverride` ignores this and always renders. Mirrors
// the /best page's own gate (`buildRankChangesByAcrId` there) so every surface
// agrees on when an organic trend is "recent" enough to show.
export const RANK_DELTA_TOP_N = 5

// We only need app + rank from each stored ranking; everything else is ignored.
const snapshotRankingsSchema = z.array(
  z.object({ app: z.number(), rank: z.number() }).passthrough(),
)

export type AppRanking = {
  appId: App['id']
  // 1-based rank in the most recent snapshot of the app's primary category.
  rank: number
  category: { id: Category['id']; slug: Category['slug'] | null; name: Category['name'] | null }
  // The resolved trend to show alongside `rank` — an editorial override (if set),
  // else the organic snapshot-derived delta (if within the top N), else none.
  rankChange?: RankChange
}

export type AppRankingSnapshotInput = {
  snapshotDate?: string | null
  rankings?: unknown
}

type ParsedSnapshot = { date: string; ranksByApp: Map<number, number> }

function parseSnapshots(snapshots: AppRankingSnapshotInput[]): ParsedSnapshot[] {
  const parsed: ParsedSnapshot[] = []
  for (const snapshot of snapshots) {
    if (!snapshot.snapshotDate) continue
    const result = snapshotRankingsSchema.safeParse(snapshot.rankings)
    if (!result.success) continue
    const ranksByApp = new Map<number, number>()
    for (const ranking of result.data) ranksByApp.set(ranking.app, ranking.rank)
    parsed.push({ date: snapshot.snapshotDate, ranksByApp })
  }
  return parsed
}

/**
 * Pure derivation shared by the server and client fetchers. `snapshots` MUST be
 * newest-first. The current rank comes from the newest snapshot; the delta is the
 * app's own most recent move — walking newest→older, the first snapshot whose rank
 * differs is its previous rank (so a *different* app moving later doesn't wipe this
 * app's arrow). Same logic the /best list uses for per-row trending.
 *
 * This only derives the ORGANIC snapshot delta — callers combine it with
 * `resolveRankDeltaOverride` via `resolveAppRankChange` to get the final
 * `rankChange`, same precedence the /best page uses.
 */
export function deriveAppRanking({
  snapshots,
  appId,
  category,
}: {
  snapshots: AppRankingSnapshotInput[]
  appId: App['id']
  category: AppRanking['category']
}): AppRanking | null {
  const parsed = parseSnapshots(snapshots)
  if (parsed.length === 0) return null

  const [newest, ...older] = parsed
  const rank = newest.ranksByApp.get(appId)
  if (rank === undefined) return null // app isn't in the current ranking

  // Track the oldest consecutive snapshot still at the current rank (when it got
  // there); stop at the first snapshot where its rank differs (its previous rank).
  let movedAt = newest.date
  let previousRank: number | null = null
  for (const snapshot of older) {
    const earlierRank = snapshot.ranksByApp.get(appId)
    if (earlierRank === undefined) break // absent earlier — no baseline to compare
    if (earlierRank !== rank) {
      previousRank = earlierRank
      break
    }
    movedAt = snapshot.date
  }

  return {
    appId,
    rank,
    category,
    rankChange: previousRank === null ? undefined : { previous: previousRank, updatedAt: movedAt },
  }
}

/**
 * An editorial override wins over the computed snapshot delta and always renders
 * (even past the top N). Mirrors `resolveRankDeltaOverride` on the /best page so
 * every surface (best list, review hero/sidebar) agrees on trend direction for a
 * given app/category.
 */
export function resolveRankDeltaOverride(
  acr: Pick<AppCategoryRecommendation, 'rankDeltaOverride' | 'updatedAt'>,
  currentRank: number,
): RankChange | undefined {
  const override = acr.rankDeltaOverride
  const direction = override?.direction

  if (direction !== RankDeltaDirection.Up && direction !== RankDeltaDirection.Down) {
    return undefined
  }

  const dir: 'up' | 'down' = direction === RankDeltaDirection.Up ? 'up' : 'down'
  const updatedAt = override?.date ?? acr.updatedAt
  const amount = override?.amount

  // No positions → show the direction only, without a magnitude.
  if (!amount || amount < 1) {
    return { direction: dir, updatedAt }
  }

  const previous = dir === 'up' ? currentRank + amount : currentRank - amount
  // A downward move from a rank shallower than the amount is impossible — fall back
  // to a direction-only indicator rather than dropping it.
  if (previous < 1) return { direction: dir, updatedAt }

  return { previous, updatedAt }
}

/**
 * Combines the organic snapshot delta with an editorial override, matching the
 * /best page's precedence: an override always wins and always shows; the organic
 * delta only shows for apps in the top N; `displayRankDelta: false` suppresses both
 * (the rank number itself still shows).
 */
export function resolveAppRankChange({
  snapshotRanking,
  acr,
  displayRankDelta,
}: {
  snapshotRanking: AppRanking
  acr: Pick<AppCategoryRecommendation, 'rankDeltaOverride' | 'updatedAt'>
  displayRankDelta: boolean
}): RankChange | undefined {
  if (!displayRankDelta) return undefined

  const override = resolveRankDeltaOverride(acr, snapshotRanking.rank)
  if (override) return override

  return snapshotRanking.rank <= RANK_DELTA_TOP_N ? snapshotRanking.rankChange : undefined
}
